import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { DriverSyncStatus } from 'components/Erp/IntegrationTrip';
import { Vehicle } from 'components/Erp/Display';
import React from 'react'
import { Users, Search, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { SectionHeading, EmptyState } from 'components/Erp/Presentation'
import styles from './AllDriversPanel.module.scss'
import DriverInfoCard from '../../../components/DriverInfoCard/DriverInfoCard'
import DriverIdentity from '../../ReportCard/components/DriverIdentity';

const AllDriversPanel = ({
  drivers,
  driversPagination,
  driversFilter,
  setDriversFilter,
  loadDriversPage,
  topBases,
  chpps,
  privatePoints,
  topDriversByLoads
}) => {
  useInterfaceLanguage();


  const getBaseName = (driver) => {
    if (driver.top_base) {
      const base = topBases?.find(tb => tb.id === driver.top_base)
      return base ? base.name : driver.top_base_name || '-'
    }
    if (driver.chpp) {
      const chpp = chpps?.find(cp => cp.id === driver.chpp)
      return chpp ? chpp.name : driver.chpp_name || '-'
    }
    if (driver.private_point) {
      const pp = privatePoints?.find(p => p.id === driver.private_point)
      return pp ? pp.name : driver.private_point_name || '-'
    }
    return '-'
  }

  React.useEffect(() => {
    const timer = setTimeout(() => {
      loadDriversPage(1, driversFilter)
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driversFilter])

  const handleSearch = () => {
    loadDriversPage(1, driversFilter)
  }

  const getStatusBadge = (status) => {
    if (!status || status === 'Свободен') return <span className={`${styles.badge} ${styles.free}`}>{tr("Свободен")}</span>
    if (status === 'Заблокирован') return <span className={`${styles.badge} ${styles.blocked}`}>{tr(status)}</span>
    if (status === 'В пути') return <span className={`${styles.badge} ${styles.route}`}>{tr(status)}</span>
    if (status === 'В погрузке') return <span className={`${styles.badge} ${styles.loading}`}>{tr(status)}</span>
    return <span className={`${styles.badge} ${styles.loading}`}>{tr(status)}</span>
  }

  const rankClass = (i) => {
    if (i === 0) return styles.rank1
    if (i === 1) return styles.rank2
    if (i === 2) return styles.rank3
    return ''
  }

  const [expandedDriver, setExpandedDriver] = React.useState(null)
  const toggleDriver = (id) => setExpandedDriver(prev => prev === id ? null : id)

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <SectionHeading icon={Users} title={tr("Транспорт")} description={tr("Транспорт, назначенные базы и текущие статусы")} />
        {tr(driversPagination?.count > 0 && (
          <span className={styles.totalBadge}>{tr("Всего: ")}{tr(driversPagination.count)}</span>
        ))}
      </div>

      {/* Топ 5 */}
      {tr(topDriversByLoads && topDriversByLoads.length > 0 && (
        <div className={styles.topSection}>
          <div className={styles.sectionLabel}><TrendingUp size={16} aria-hidden="true" />{tr("Топ 5 — по количеству погрузок")}</div>
          <div className={styles.topList}>
            {topDriversByLoads.slice(0, 5).map((driver, i) => (
              <div key={driver.id || i} className={`${styles.topRow} ${rankClass(i)}`}>
                <span className={styles.topRank}>
                  {tr(i + 1)}
                </span>
                <Vehicle number={driver.vehicle_number} model={driver.vehicle_model} />
                <div>
                  <div className={styles.topCount}>{tr(driver.loads_count || 0)}</div>
                  <div className={styles.topCountSub}>{tr("погрузок")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Search */}
      <div className={styles.filtersRow}>
        <Search size={16} aria-hidden="true" />
        <input
          className={styles.searchInput}
          placeholder={tr("Поиск по водителю, номеру машины, базе...")}
          value={driversFilter}
          onChange={(e) => setDriversFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
        />
      </div>

      {/* Table */}
      {drivers && drivers.length ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{tr("Автомобиль")}</th>
                <th>{tr("Водитель")}</th>
                <th>№</th>
                <th>{tr("Марка")}</th>
                <th>{tr("База")}</th>
                <th>{tr("Телефон")}</th>
                <th>{tr("Погрузок")}</th>
                <th>{tr("Активность")}</th>
                <th>{tr("Статус")}</th><th>{tr("Синхронизация")}</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver, index) => {
                const globalIndex = ((driversPagination?.page || 1) - 1) * 25 + index + 1
                return (
                  <React.Fragment key={driver.id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleDriver(driver.id)}
                  >
                    <td data-label={tr("Автомобиль")}><Vehicle number={driver.vehicle_number} model={driver.vehicle_model} /></td>
                    <td data-label={tr("Водитель")} className={styles.nameCell}><DriverIdentity name={driver.employee_name} phone={driver.employee_phone || driver.phone} mobilePhone /></td>
                    <td data-label={tr("№")} className={styles.numCell}>{tr(globalIndex)}</td>
                    <td data-label={tr("Марка")}>{tr(driver.vehicle_model || '—')}</td>
                    <td data-label={tr("База")}>{getBaseName(driver)}</td>
                    <td data-label={tr("Телефон")} className={styles.separatePhone}>{tr(driver.employee_phone || driver.phone || '—')}</td>
                    <td data-label={tr("Погрузок")} className={styles.countCell}>{tr(driver.loads_count || 0)}</td>
                    <td data-label={tr("Активность")}>
                      <span className={`${styles.badge} ${driver.is_active ? styles.active : styles.inactive}`}>
                        {driver.is_active ? tr("Активен") : tr("Неактивен")}
                      </span>
                    </td>
                    <td data-label={tr("Статус")}>{getStatusBadge(driver.status)}</td><td data-label={tr("Синхронизация")}><DriverSyncStatus driver={driver} /></td>
                  </tr>
                  {tr(expandedDriver === driver.id && (
                    <tr>
                      <td colSpan={10} style={{ padding: '12px 16px', background: "var(--theme-surface-muted, #f8fafc)" }}>
                        <DriverInfoCard driver={driver} />
                      </td>
                    </tr>
                  ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon={Users} title={tr("Транспорт не найден")} description={tr("Измените поиск по имени, машине или базе.")} compact />
      )}

      {tr(driversPagination?.count > 0 && (
        <div className={styles.pagination}>
          <button className={styles.pageBtn} disabled={!driversPagination.previous}
            onClick={() => loadDriversPage((driversPagination.page || 1) - 1, driversFilter)}>
            <ChevronLeft size={15} aria-hidden="true" />{tr("Пред.")}</button>
          <span>{tr("Стр. ")}{tr(driversPagination.page || 1)}{tr(" из ")}{Math.ceil(driversPagination.count / 25)}</span>
          <button className={styles.pageBtn} disabled={!driversPagination.next}
            onClick={() => loadDriversPage((driversPagination.page || 1) + 1, driversFilter)}>{tr("След.")}<ChevronRight size={15} aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  )
}

export default AllDriversPanel
